import React from 'react'
import classNames from 'classnames'
import { format } from 'date-fns'
import { Avatar, Image, StarRating } from '@deal/components'
import Purchased from './purchased.svg'
import { getSellableUrl, formatSellablePricing } from '#src/services/sellable'
import { ConsumerExpertReviewFragment } from '#src/generated/graphql'
import { Link } from 'react-router-dom'
import styles from './styles.css'

interface CardProps {
  className?: string
}

interface ActivityFeedCardProps extends CardProps {
  activityFeedItem: ConsumerExpertReviewFragment
}

const NoActivityCard: React.SFC<CardProps> = ({ className, children }) => {
  return (
    <div className={classNames(styles.item, className)}>
      <div className={styles.itemContent}>
        <div className={styles.noActivity}>{children}</div>
      </div>
    </div>
  )
}

const ActivityFeedCard: React.SFC<ActivityFeedCardProps> = ({ activityFeedItem, className }) => {
  const verifiedPurchases = activityFeedItem.verifiedPurchases

  const expertPhoto = activityFeedItem.expert.expertProfile
    ? activityFeedItem.expert.expertProfile.profilePhoto
    : null

  return (
    <div className={classNames(styles.item, className)}>
      <div className={styles.itemHeader}>
        <div className={styles.avatarContainer}>
          <Link to={`/experts/${activityFeedItem.expert.vanityId}`}>
            <Avatar
              name={`${activityFeedItem.expert.firstName} ${activityFeedItem.expert.lastName}`}
              src={expertPhoto ? expertPhoto.url : undefined}
            />
          </Link>
          {activityFeedItem.expert.expertAttributes &&
            activityFeedItem.expert.expertAttributes.acceptingNewRequests && (
              <div className={styles.presenceBadge} />
            )}
        </div>

        <div className={styles.headerDetails}>
          <div>
            Expert:{' '}
            <Link
              to={`/experts/${activityFeedItem.expert.vanityId || activityFeedItem.expert.id}`}
              className={styles.expertName}
            >
              {activityFeedItem.expert.firstName} {activityFeedItem.expert.lastName}
            </Link>
          </div>
          <div className={styles.reviewer}>Reviewer: {activityFeedItem.consumerName}</div>
        </div>
      </div>

      <div className={styles.itemContent}>
        <div>
          <div className={styles.reviewHeader}>
            <div className={styles.reviewHeaderStars}>
              <StarRating rating={activityFeedItem.rating} />
            </div>
            <span className={styles.reviewHeadline}>
              {activityFeedItem.headline
                ? activityFeedItem.headline
                : `${activityFeedItem.consumerName}'s experience with ${
                    activityFeedItem.expert.firstName
                  }`}
            </span>
          </div>
          {activityFeedItem.comments && (
            <div
              className={styles.reviewBody}
              dangerouslySetInnerHTML={{
                __html: activityFeedItem.comments
              }}
            />
          )}
          <div className={styles.reviewFooter}>
            Verified Review | Submitted {format(activityFeedItem.reviewedAt, 'MMM d, YYYY')}
          </div>
          {verifiedPurchases.length > 0 && (
            <div className={styles.reviewSellablesContainer}>
              <div className={styles.reviewSellables}>
                {verifiedPurchases.map(sellable => {
                  const sellableImage = sellable.images.length > 0 ? sellable.images[0] : ''
                  const sellablePricing = formatSellablePricing(sellable, {
                    percentSavingsDisplayThreshold: 5
                  })

                  if (!sellableImage) {
                    return null
                  }

                  return (
                    <div className={styles.reviewSellable} key={sellable.id}>
                      <span className={styles.purchased}>
                        <Purchased className={styles.purchasedIcon} /> Purchased
                      </span>
                      <Link to={getSellableUrl(sellable)}>
                        <div className={styles.sellableImage}>
                          <Image
                            src={sellableImage.url}
                            width={164}
                            height={164}
                            imgixParams={{
                              fit: 'crop',
                              h: '164',
                              w: '164'
                            }}
                          />
                        </div>
                        <div className={styles.sellableDetails}>
                          <div className={styles.sellableTitle}>{sellable.title}</div>
                          {sellablePricing && (
                            <div className={styles.sellablePrice}>
                              <div className={styles.sellablePriceValue}>
                                <strong>{sellablePricing.price}</strong>
                                {sellablePricing.msrp && `was ${sellablePricing.msrp}`}
                              </div>
                              {sellablePricing.savingsPercentage && (
                                <div className={styles.sellablePriceDiscount}>
                                  {sellablePricing.savingsPercentage} off
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { NoActivityCard }

export default ActivityFeedCard
